import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Grid,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { format, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PageLayout from '../PageLayout';
import { DEFAULT_PROSPERITY_CATEGORIES } from '../../utils/prosperityDefaults';
import { months } from '../../utils/dateUtils';
import { alpha } from '@mui/material/styles';
import { getContrastText } from '../../utils/colorUtils';

const INITIAL_PROSPERITY = {
  transactions: [],
  categories: DEFAULT_PROSPERITY_CATEGORIES,
  budgetLimitByMonth: {},
};

function ensureProsperity(userRef) {
  return getDoc(userRef).then((snap) => {
    const data = snap.exists() ? snap.data() : {};
    const p = data.prosperity || INITIAL_PROSPERITY;
    const defaultIds = new Set(DEFAULT_PROSPERITY_CATEGORIES.map((c) => c.id));
    const userCategories = p.categories && p.categories.length > 0 ? p.categories : [];
    const mergedCategories = [
      ...DEFAULT_PROSPERITY_CATEGORIES,
      ...userCategories.filter((uc) => !defaultIds.has(uc.id) && uc.id !== 'essencial'),
    ];
    return { ...p, categories: mergedCategories };
  });
}

const now = new Date();
const CURRENT_MONTH = now.getMonth();
const CURRENT_YEAR = now.getFullYear();

export default function ProsperityBudget({ customColors }) {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [prosperity, setProsperity] = useState(INITIAL_PROSPERITY);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null); // transaction being edited, or null for new
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [form, setForm] = useState({
    type: 'expense',
    amount: '',
    categoryId: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    note: '',
  });
  const [budgetLimitInput, setBudgetLimitInput] = useState('');
  const [showBudgetInput, setShowBudgetInput] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(null); // null = todos, ou id da categoria

  const { transactions = [], categories = [], budgetLimitByMonth = {} } = prosperity;

  useEffect(() => {
    if (!currentUser?.uid) return;
    ensureProsperity(doc(db, 'users', currentUser.uid)).then(setProsperity);
  }, [currentUser?.uid]);

  const currentMonthKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
  const budgetLimit = budgetLimitByMonth[currentMonthKey] || 0;
  const start = startOfMonth(new Date(selectedYear, selectedMonth));
  const end = endOfMonth(new Date(selectedYear, selectedMonth));
  let receita = 0;
  let despesas = 0;
  const monthTransactions = [];
  for (const t of transactions) {
    const d = t.date ? new Date(t.date + 'T12:00:00') : null;
    if (!d || !isWithinInterval(d, { start, end })) continue;
    monthTransactions.push(t);
    const amount = Math.abs(Number(t.amount) || 0);
    if (t.type === 'income') receita += amount;
    else despesas += amount;
  }
  const pctGasto = budgetLimit > 0 ? Math.min(100, (despesas / budgetLimit) * 100) : 0;
  const saldo = receita - despesas;
  const expenseCategories = categories.filter((c) => c.type === 'expense');
  const incomeCategories = categories.filter((c) => c.type === 'income');

  const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

  const handleSaveTransaction = async () => {
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: 'Informe um valor válido.', severity: 'warning' });
      return;
    }
    const categoryId = form.categoryId || (form.type === 'income' ? incomeCategories[0]?.id : expenseCategories[0]?.id);
    if (!categoryId) {
      setSnackbar({ open: true, message: 'Escolha uma categoria.', severity: 'warning' });
      return;
    }
    const isEdit = !!editingTransaction;
    const updatedTransaction = {
      id: editingTransaction?.id ?? Date.now().toString(),
      type: form.type,
      amount: Math.abs(parseFloat(String(form.amount).replace(',', '.'))),
      categoryId,
      date: form.date,
      note: (form.note || '').trim(),
      createdAt: editingTransaction?.createdAt ?? new Date().toISOString(),
    };
    const updated = {
      ...prosperity,
      transactions: isEdit
        ? transactions.map((tr) => (tr.id === editingTransaction.id ? updatedTransaction : tr))
        : [updatedTransaction, ...transactions],
    };
    setProsperity(updated);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { prosperity: updated });
      setSnackbar({ open: true, message: isEdit ? 'Lançamento atualizado!' : 'Lançamento salvo!', severity: 'success' });
      setOpenDialog(false);
      setEditingTransaction(null);
      setForm({ type: 'expense', amount: '', categoryId: '', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Erro ao salvar.', severity: 'error' });
    }
  };

  const handleOpenEdit = (t) => {
    const dateStr = t.date && t.date.length >= 10 ? t.date.slice(0, 10) : format(new Date(), 'yyyy-MM-dd');
    const categoryId = t.categoryId === 'essencial' ? 'compras' : (t.categoryId || '');
    setEditingTransaction(t);
    setForm({
      type: t.type || 'expense',
      amount: String(t.amount ?? ''),
      categoryId,
      date: dateStr,
      note: t.note || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTransaction(null);
    setForm({ type: 'expense', amount: '', categoryId: '', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
  };

  const handleDeleteTransaction = async (id) => {
    const updated = {
      ...prosperity,
      transactions: transactions.filter((t) => t.id !== id),
    };
    setProsperity(updated);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { prosperity: updated });
      setSnackbar({ open: true, message: 'Lançamento removido.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Erro ao remover.', severity: 'error' });
    }
  };

  const handleSaveBudgetLimit = async () => {
    const value = parseFloat(String(budgetLimitInput).replace(',', '.'));
    if (isNaN(value) || value < 0) return;
    const updated = {
      ...prosperity,
      budgetLimitByMonth: { ...budgetLimitByMonth, [currentMonthKey]: value },
    };
    setProsperity(updated);
    setShowBudgetInput(false);
    setBudgetLimitInput('');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { prosperity: updated });
      setSnackbar({ open: true, message: 'Limite do mês definido.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Erro ao salvar limite.', severity: 'error' });
    }
  };

  const getCategoryLabel = (id) => {
    if (id === 'essencial') return 'Compras';
    return categories.find((c) => c.id === id)?.label || id;
  };

  const renderCategoryIcon = (c) => {
    if (c.icon === 'TRENDING_UP') {
      return <TrendingUpIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />;
    }
    if (c.icon === 'TRENDING_DOWN') {
      return <TrendingDownIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />;
    }
    return <span style={{ marginRight: 4 }}>{c.icon}</span>;
  };

  // Lançamentos sempre em ordem cronológica pela data do lançamento
  const sortedMonthTransactions = [...monthTransactions].sort((a, b) => {
    const da = a.date ? new Date(a.date + 'T12:00:00') : new Date(0);
    const db = b.date ? new Date(b.date + 'T12:00:00') : new Date(0);
    return da - db;
  });

  const displayedTransactions = categoryFilter
    ? sortedMonthTransactions.filter((t) => t.categoryId === categoryFilter)
    : sortedMonthTransactions;
  const primaryColor = customColors?.primary || theme.palette.primary.main;
  const primaryTextColor = customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText;

  return (
    <PageLayout title="Orçamento Pessoal" hideAddButton customColors={customColors}>
      <Box sx={{ maxWidth: 720, mx: 'auto', pb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="orcamento-mes-label">Mês</InputLabel>
            <Select
              labelId="orcamento-mes-label"
              value={selectedMonth}
              label="Mês"
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              sx={{
                '& .MuiSelect-select': { py: 1.25 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                }
              }}
            >
              {months.map((monthName, index) => (
                <MenuItem key={monthName} value={index}>{monthName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="number"
            label="Ano"
            value={selectedYear}
            onChange={(e) => {
              const y = parseInt(e.target.value, 10);
              if (!isNaN(y) && y >= 2000 && y <= 2100) setSelectedYear(y);
            }}
            size="small"
            inputProps={{ min: 2000, max: 2100, step: 1 }}
            sx={{ width: 100 }}
          />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingTransaction(null);
              setForm({ type: 'expense', amount: '', categoryId: '', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
              setOpenDialog(true);
            }}
            sx={{
              ml: 'auto',
              bgcolor: primaryColor,
              color: primaryTextColor,
              border: '1px solid',
              borderColor: primaryColor,
              '&:hover': { bgcolor: primaryColor, opacity: 0.9 }
            }}
          >
            Novo lançamento
          </Button>
        </Box>

        {/* Dashboard: Entradas, Saídas, Saldo */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                height: '100%',
                borderLeft: 4,
                borderColor: 'success.main',
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingUpIcon sx={{ color: 'success.main', fontSize: 28 }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                    Entradas
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                  {formatCurrency(receita)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {months[selectedMonth]} de {selectedYear}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                height: '100%',
                borderLeft: 4,
                borderColor: 'error.main',
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingDownIcon sx={{ color: 'error.main', fontSize: 28 }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                    Saídas
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                  {formatCurrency(despesas)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {months[selectedMonth]} de {selectedYear}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                height: '100%',
                borderLeft: 4,
                borderColor: saldo >= 0 ? 'success.main' : 'error.main',
                bgcolor: (theme) =>
                  alpha(saldo >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.08),
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalanceWalletIcon
                    sx={{ color: saldo >= 0 ? 'success.main' : 'error.main', fontSize: 28 }}
                  />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                    Saldo
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: saldo >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 700,
                  }}
                >
                  {saldo >= 0 ? '+' : ''}{formatCurrency(saldo)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Entradas − Saídas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {budgetLimit > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Você já gastou {pctGasto.toFixed(0)}% do orçamento planejado para {months[selectedMonth]} de {selectedYear}
                </Typography>
                {!showBudgetInput && (
                  <Button size="small" onClick={() => setShowBudgetInput(true)}>
                    Alterar limite
                  </Button>
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={pctGasto}
                color={pctGasto >= 100 ? 'error' : pctGasto >= 80 ? 'warning' : 'primary'}
                sx={{ height: 10, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {formatCurrency(despesas)} de {formatCurrency(budgetLimit)}
              </Typography>
              {showBudgetInput && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField
                    size="small"
                    label="Limite do mês (R$)"
                    type="number"
                    value={budgetLimitInput}
                    onChange={(e) => setBudgetLimitInput(e.target.value)}
                    placeholder={String(budgetLimit)}
                  />
                  <Button size="small" variant="contained" onClick={handleSaveBudgetLimit}>
                    Salvar
                  </Button>
                  <Button size="small" onClick={() => setShowBudgetInput(false)}>Cancelar</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        {budgetLimit === 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Defina um limite de gastos para {months[selectedMonth]} de {selectedYear} e acompanhe com a barra de progresso.
              </Typography>
              {!showBudgetInput ? (
                <Button size="small" variant="outlined" onClick={() => setShowBudgetInput(true)}>
                  Definir limite do mês
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Limite do mês (R$)"
                    type="number"
                    value={budgetLimitInput}
                    onChange={(e) => setBudgetLimitInput(e.target.value)}
                  />
                  <Button size="small" variant="contained" onClick={handleSaveBudgetLimit}>
                    Salvar
                  </Button>
                  <Button size="small" onClick={() => setShowBudgetInput(false)}>Cancelar</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
          Categorias
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
          <Button
            size="small"
            variant={categoryFilter === null ? 'contained' : 'outlined'}
            onClick={() => setCategoryFilter(null)}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              minHeight: 32,
              bgcolor: categoryFilter === null ? primaryColor : 'transparent',
              color: categoryFilter === null ? primaryTextColor : 'text.primary',
              borderColor: primaryColor,
              '&:hover': {
                bgcolor: categoryFilter === null ? primaryColor : 'action.hover',
                borderColor: primaryColor
              }
            }}
          >
            Todos
          </Button>
          {expenseCategories.map((c) => {
            const selected = categoryFilter === c.id;
            return (
              <Button
                key={c.id}
                size="small"
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => setCategoryFilter(selected ? null : c.id)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  minHeight: 32,
                  bgcolor: selected ? primaryColor : 'transparent',
                  color: selected ? primaryTextColor : 'text.primary',
                  borderColor: primaryColor,
                  '&:hover': {
                    bgcolor: selected ? primaryColor : 'action.hover',
                    borderColor: primaryColor
                  }
                }}
              >
                {renderCategoryIcon(c)}{c.label}
              </Button>
            );
          })}
          {incomeCategories.map((c) => {
            const selected = categoryFilter === c.id;
            return (
              <Button
                key={c.id}
                size="small"
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => setCategoryFilter(selected ? null : c.id)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  minHeight: 32,
                  bgcolor: selected ? primaryColor : 'transparent',
                  color: selected ? primaryTextColor : 'text.primary',
                  borderColor: primaryColor,
                  '&:hover': {
                    bgcolor: selected ? primaryColor : 'action.hover',
                    borderColor: primaryColor
                  }
                }}
              >
                {renderCategoryIcon(c)}{c.label}
              </Button>
            );
          })}
        </Box>

        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Lançamentos de {months[selectedMonth]} de {selectedYear}
          {categoryFilter ? ` · ${getCategoryLabel(categoryFilter)}` : ''}
        </Typography>
        {displayedTransactions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {categoryFilter
              ? `Nenhum lançamento em ${months[selectedMonth]} de ${selectedYear} na categoria ${getCategoryLabel(categoryFilter)}.`
              : `Nenhum lançamento em ${months[selectedMonth]} de ${selectedYear}. Use "Novo lançamento" para começar.`}
          </Typography>
        ) : (
          <List dense>
            {displayedTransactions.map((t) => (
              <ListItem
                key={t.id}
                onClick={() => handleOpenEdit(t)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTransaction(t.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${getCategoryLabel(t.categoryId)} · ${format(new Date(t.date + 'T12:00:00'), "dd/MM", { locale: ptBR })}`}
                  secondary={t.note || undefined}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <Typography variant="body2" sx={{ color: t.type === 'income' ? 'success.main' : 'error.main', fontWeight: 500 }}>
                  {t.type === 'income' ? '+' : ''}{formatCurrency(t.type === 'income' ? t.amount : Math.abs(t.amount))}
                </Typography>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTransaction ? 'Editar lançamento' : 'Novo lançamento'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={form.type}
                label="Tipo"
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value, categoryId: '' }))}
              >
                <MenuItem value="income">Receita (+)</MenuItem>
                <MenuItem value="expense">Despesa (−)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Valor (R$)"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              size="small"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={form.categoryId}
                label="Categoria"
                onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              >
                {(form.type === 'income' ? incomeCategories : expenseCategories).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {renderCategoryIcon(c)}{c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Data"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            />
            <TextField
              label="Observação (opcional)"
              fullWidth
              size="small"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveTransaction}>
            {editingTransaction ? 'Atualizar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </PageLayout>
  );
}
